<template>
  <div v-if="anythingToDisplay" class="page-header" :class="[backgroundColorVariant, { 'bottom-border': bottomBorder }]"
       :style="{ backgroundColor, color: textColor }">
    <stepper v-if="onlyStepper" v-bind="stepperProps"/>
    <div v-else class="actions-container">
      <div class="actions-with-logo">
        <transition-group name="fade-actions" class="actions" :class="{ 'page-header-transition': true, disabled: !animateChanges }">
          <action-icon v-for="action in rightActions" :key="action.type" :id="`header_action_${action.type}`"
                       :action-type="action.type" @click="action.onClick"/>
        </transition-group>
        <slot name="header-wildcard-content"/>
        <transition-group name="fade-actions" class="actions" :class="{ 'page-header-transition': true, disabled: !animateChanges }">
          <action-icon v-for="action in leftActions" :key="action.type" :id="`header_action_${action.type}`"
                       :action-type="action.type" @click="action.onClick"/>
        </transition-group>
        <transition name="fade-actions">
          <div v-if="showLogo" class="logo-container" :class="{ clickable: !!onLogoClick }" @click="logoClicked" @keyup.enter="logoClicked"
               :tabindex="onLogoClick ? 0 : undefined">
          <colored-svg class="logo" :svg-src="require('../assets/header-logo.svg')" :color="logoColor"/>
          </div>
        </transition>
      </div>
      <stepper v-if="stepperProps" class="margin-top-sm" v-bind="stepperProps"/>
    </div>
  </div>
</template>

<script>
import ColoredSvg from '../components/ColoredSVG.vue';
import ActionIcon from '../components/ActionIcon/ActionIcon.vue';
import Stepper, { StepperProps } from '../components/StepperV2.vue';
import Colors from '../scss/riseup-js-colors.module.scss';
import { COLOR_VARIANTS } from '../utils/layout-consts';

export const PageHeaderProps = {
  stepperProps: StepperProps,
  leftActions: {
    type: Array,
    required: false,
  },
  rightActions: {
    type: Array,
    required: false,
  },
  backgroundColor: {
    type: String,
    required: false,
  },
  backgroundColorVariant: {
    type: String,
    validator: value => COLOR_VARIANTS.includes(value),
  },
  textColor: {
    type: String,
    default: Colors.riseupBlack,
  },
  showLogo: {
    type: Boolean,
    required: false,
  },
  logoColor: {
    type: String,
    default: Colors.riseupBlack,
  },
  onLogoClick: {
    type: Function,
    required: false,
  },
  bottomBorder: {
    type: Boolean,
    default: false,
  },
  animateChanges: {
    type: Boolean,
    default: false,
  },
};

export default {
  name: 'PageHeader',
  components: {
    ColoredSvg,
    ActionIcon,
    Stepper,
  },
  props: PageHeaderProps,
  computed: {
    hasActions() {
      return this.leftActions || this.rightActions || this.showLogo;
    },
    anythingToDisplay() {
      return this.hasActions || this.stepperProps;
    },
    onlyStepper() {
      return this.stepperProps && !this.hasActions;
    },
  },
  methods: {
    logoClicked() {
      this.onLogoClick?.();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../scss/spacings';
@import '../scss/riseup-colors';
@import '../scss/riseup-z-index';

$bottom-border-width: 1px;
.page-header {
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  padding: $ri-spacing-sm $ri-spacing-ml;
  z-index: $app-header;
  background-color: $riseup-white;

  @each $variant, $color in $variant-to-color {
    &.#{$variant} {
      background-color: $color;
    }
  }

  &.bottom-border {
    border-bottom: $bottom-border-width solid rgba($riseup_black, 0.13);
    padding-bottom: calc(#{$ri-spacing-sm} - #{$bottom-border-width}); // subtract border width
  }

  .actions-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;

    .actions-with-logo {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 100%;

      .page-header-transition:not(.disabled) {
        .fade-actions-enter, .fade-actions-leave-to {
          opacity: 0;
          transition: opacity 0.3s;
        }
      }

      .actions {
        display: flex;
        flex-direction: row;
        column-gap: 4px;
      }

      .logo {
        width: 80px;
        min-width: 80px;
        height: 22px;
        margin-bottom: 2px;
      }
      .logo-container {
        &.clickable {
          cursor: pointer;
        }
        &.fade-actions-enter, &.fade-actions-leave-to {
        opacity: 0;
        transition: opacity 0.3s;
        }
      }
    }
  }
}
</style>
